import axios from 'axios';

import { filtersI, ProfilesListResult } from '@/interfaces/profile';

import { getValue } from './localStorage';

const PROFILES_V2_URL = process.env.REACT_APP_API_URL + '/api/v2/profiles';

const profilesV2Headers = () => ({
  Authorization: `Bearer ${getValue('access_token')}`,
});

const serializeProfileListParams = (
  params: Record<string, string | number | boolean>
) =>
  Object.entries(params)
    .filter(([key, value]) => {
      if (value === undefined || value === null) return false;
      if (value === '' && key !== 'active_subscription') return false;
      return true;
    })
    .map(([key, value]) =>
      value === ''
        ? `${encodeURIComponent(key)}=`
        : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join('&');

const fetchV2ProfilesList = async (
  params: Record<string, string | number | boolean>
): Promise<ProfilesListResult> => {
  const res = await axios.get<ProfilesListResult>(PROFILES_V2_URL, {
    headers: profilesV2Headers(),
    params,
    paramsSerializer: serializeProfileListParams,
  });

  return {
    items: res.data.items ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    size: res.data.size ?? 0,
  };
};

const buildProfileListParams = (
  page: number,
  size: number,
  extra: Record<string, string | number | boolean> = {},
  filters?: filtersI
) => {
  const params: Record<string, string | number | boolean> = {
    page,
    size,
    ...extra,
  };

  if (filters?.clientStatus) {
    params.type = filters.clientStatus;
  }

  // Dates go into active_subscription (v1 parity + v2 docs):
  // all: active_subscription=
  // from-to: <DATE>|<DATE> | from: <DATE>| | to: |<DATE>
  if (filters?.subStatus === 'canceled') {
    params.canceled_subscription = true;
  } else if (filters?.from || filters?.to) {
    if (filters.from && filters.to) {
      params.active_subscription = `${filters.from}|${filters.to}`;
    } else if (filters.from) {
      params.active_subscription = `${filters.from}|`;
    } else {
      params.active_subscription = `|${filters.to}`;
    }
  } else if (filters?.subStatus === 'active') {
    params.active_subscription = '';
  }

  const phone = filters?.phone?.trim();
  if (phone) {
    params.phone = phone;
  } else {
    delete params.phone;
  }

  return params;
};

export const getAllProfiles = async (
  page: number = 1,
  maxResults: number,
  filters?: filtersI
) => {
  return fetchV2ProfilesList(
    buildProfileListParams(page, maxResults, {}, filters)
  );
};

export const getProfileByNickname = async (
  page: number,
  nickname: string,
  filters?: filtersI
) => {
  return fetchV2ProfilesList(
    buildProfileListParams(page, 15, { nickname }, filters)
  );
};

export const getProfileById = async (id: string) => {
  const res = await axios.get(`${PROFILES_V2_URL}/${id}`, {
    headers: profilesV2Headers(),
  });

  return res.data;
};

export const getProfileByPhone = async (phone: string) => {
  const params: Record<string, string | number | boolean> = {
    page: 1,
    size: 50,
  };

  const normalizedPhone = phone?.trim();
  if (normalizedPhone) {
    params.phone = normalizedPhone;
  }

  return fetchV2ProfilesList(params);
};

/** Exact nickname match (page 1). Use in search dropdowns. */
export const searchByNickname = (search = '') =>
  getProfileByNickname(1, search);
